/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the connector resource of the user-management service.
 *
 * Features:
 * - `load$()` — fetches connectors for an organisation + type via a cached,
 *   deduplicated GET request using `ApiClient.getShared()`.
 * - `loadOne$()` — fetches a single connector by ID (Pattern A).
 * - `create$()` — creates a connector and invalidates the list cache (Pattern B).
 * - `update$()` — updates a connector and invalidates the list cache (Pattern A).
 * - `deleteConnector$()` — deletes a connector and invalidates the list cache (Pattern A).
 * - `invalidateCache()` — evicts all connector cache entries for an organisation.
 * - Deprecated Promise wrappers kept for backward compatibility.
 */

import {HttpParams} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {datadogLogs} from '@datadog/browser-logs';
import {firstValueFrom, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import {environment} from '../environments/environment';
import {ConnectorConfig} from '../models/ConnectorConfig';
import {ApiClient} from './api-client.service';
import {RequestCacheService} from './request-cache.service';

/** Shape of the raw list response from the user-management service. */
interface ConnectorListResponse {
  readonly data: ConnectorConfig[];
}

/** Shape of the raw single-item response. */
interface ConnectorResponse {
  readonly data: ConnectorConfig;
}

/** Base URL segment for the connector resource. */
const connectorsUrl = (orgId: string) =>
  `${environment.backend.user_host}/organizations/${orgId}/connectors`;

/**
 * Builds the list URL including query params, mirroring the original logic:
 * both `organizationId` and `type` are only appended when non-empty.
 */
function buildListUrl(orgId: string, type: 'provider' | 'consumer'): string {
  let params = new HttpParams();
  if (orgId) {
    params = params.append('organizationId', orgId);
  }
  if (type) {
    params = params.append('type', type);
  }
  return `${connectorsUrl(orgId)}?${params.toString()}`;
}

@Injectable({
  providedIn: 'root',
})
export class ConnectorService {
  private readonly apiClient = inject(ApiClient);
  private readonly cache = inject(RequestCacheService);

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Returns connectors for the given organisation and type.
   *
   * The underlying GET is deduplicated and cached for 5 minutes via
   * `ApiClient.getShared()`. Call `invalidateCache()` after any mutation.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   */
  load$(
    organizationId: string,
    type: 'provider' | 'consumer',
  ): Observable<ConnectorConfig[]> {
    return this.apiClient
      .getShared<ConnectorListResponse>(buildListUrl(organizationId, type))
      .pipe(map((response) => response.data));
  }

  /**
   * Returns a single connector by ID.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   */
  loadOne$(
    organizationId: string,
    connectorId: string,
  ): Observable<ConnectorConfig> {
    return this.apiClient
      .get<ConnectorResponse>(`${connectorsUrl(organizationId)}/${connectorId}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Creates a new connector.
   *
   * Logs the created connector ID via Datadog and invalidates the list cache.
   * The caller is responsible for handling validation errors (Pattern B).
   *
   * @returns An `Observable` emitting the created `ConnectorConfig`.
   */
  create$(
    organizationId: string,
    connectorConfig: ConnectorConfig,
  ): Observable<ConnectorConfig> {
    return this.apiClient
      .post<ConnectorResponse>(connectorsUrl(organizationId), connectorConfig)
      .pipe(
        map((response) => response.data),
        tap((created) => {
          datadogLogs.logger.info(
            `connector created: ${created.id} | ${JSON.stringify(created)}`,
          );
          this.invalidateCache(organizationId);
        }),
      );
  }

  /**
   * Updates an existing connector and invalidates the list cache.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   */
  update$(
    organizationId: string,
    connectorConfig: ConnectorConfig,
  ): Observable<void> {
    return this.apiClient
      .patch<void>(
        `${connectorsUrl(organizationId)}/${connectorConfig.id}`,
        connectorConfig,
      )
      .pipe(tap(() => this.invalidateCache(organizationId)));
  }

  /**
   * Deletes a connector and invalidates the list cache.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   */
  deleteConnector$(
    organizationId: string,
    connectorId: string,
  ): Observable<void> {
    return this.apiClient
      .delete<void>(`${connectorsUrl(organizationId)}/${connectorId}`)
      .pipe(tap(() => this.invalidateCache(organizationId)));
  }

  /**
   * Invalidates all cached connector list entries for the given organisation.
   *
   * Call this after any mutation (create, update, delete) that changes the
   * connector data so that the next `load$()` call fetches fresh data.
   */
  invalidateCache(organizationId: string): void {
    this.cache.invalidateByPrefix(connectorsUrl(organizationId));
  }

  // ─── Deprecated Promise wrappers (backward-compat) ──────────────────────────

  /**
   * @deprecated Use `load$()` instead.
   * The `force` parameter is ignored — call `invalidateCache()` explicitly
   * before subscribing to force a fresh fetch.
   */
  async load(
    organizationId: string,
    type: 'provider' | 'consumer',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _force = false,
  ): Promise<ConnectorConfig[]> {
    return firstValueFrom(this.load$(organizationId, type));
  }

  /** @deprecated Use `loadOne$()` instead. */
  async loadOne(
    organizationId: string,
    connectorId: string,
  ): Promise<ConnectorConfig> {
    return firstValueFrom(this.loadOne$(organizationId, connectorId));
  }

  /** @deprecated Use `create$()` instead. */
  async create(
    organizationId: string,
    connectorConfig: ConnectorConfig,
  ): Promise<ConnectorConfig> {
    return firstValueFrom(this.create$(organizationId, connectorConfig));
  }

  /** @deprecated Use `update$()` instead. */
  async update(
    organizationId: string,
    connectorConfig: ConnectorConfig,
  ): Promise<void> {
    return firstValueFrom(this.update$(organizationId, connectorConfig));
  }

  /** @deprecated Use `deleteConnector$()` instead. */
  deleteConnector(organizationId: string, connectorId: string): Promise<void> {
    return firstValueFrom(this.deleteConnector$(organizationId, connectorId));
  }
}
